import React from 'react';

import { Skeleton } from '@travel/ui';
import { cx } from '@travel/utils';

import styles from './withSkeletonLoading.module.scss';

export type Props = {
  className?: string;
  /** Flag to define whether the skeleton paragraph will be displayed or not */
  isLoading?: boolean;
  /** Number to define the row for paragraph */
  row?: number;
  /** It define paragraph length */
  length?: 'short' | 'long';
  /** Number to define the count of skeleton component */
  count?: number;
  /** Node to be rendered when isLoading is false */
  children?: React.ReactNode;
  /** Skeleton type */
  customStyle?: 'text' | 'textWithImageRow' | 'textWithImageColumn' | 'image';
  /** Skeleton item className */
  skeletonItemClassName?: string;
  /** Skeleton item's image className */
  skeletonImageClassName?: string;
  /** Direction of flex wrapper */
  direction?: 'column' | 'row';
};

// to override min-height/min-width default <Skeleton /> inline style
const OVERRIDE_PROP = { width: 'unset', height: 'unset' };

function WithSkeletonLoading({
  length = 'short',
  count = 1,
  children = null,
  customStyle = 'text',
  direction = 'column',
  ...props
}: Props) {
  if (!props.isLoading) return <>{children}</>;

  return (
    <div
      className={cx(props.className, styles.wrapper, styles[`direction--${direction}`])}
      data-testid="withSkeletonLoading-wrapper"
    >
      {Array(count)
        .fill(0)
        .map((_item, index) => (
          <div
            key={index}
            className={cx(
              props.skeletonItemClassName,
              styles.item,
              styles[`layout-style--${customStyle}`],
            )}
          >
            {customStyle !== 'text' && (
              <Skeleton
                data-testid="skeleton-wrapper"
                className={cx(
                  styles.image,
                  props.skeletonImageClassName,
                  styles[`image-style--${customStyle}`],
                )}
                type="image"
                {...OVERRIDE_PROP}
              />
            )}
            {customStyle !== 'image' && (
              <Skeleton
                data-testid="skeleton-wrapper"
                key={index}
                type="paragraph"
                row={props.row}
                length={length}
                {...OVERRIDE_PROP}
              />
            )}
          </div>
        ))}
    </div>
  );
}

export default WithSkeletonLoading;
